import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout/PageLayout';
import { createProducerProfile, uploadFile, updateProducerProfile } from '../../api';
import { reissueToken } from '../../api/authApi';
import { useAuth } from '../../contexts/AuthContext';
import styles from './ProducerOnboardingPage.module.css';

const ACTIVITY_TAGS = ['영화', '드라마', '예능', '다큐멘터리', '유튜브', '숏폼', '광고', '뮤직비디오', '브랜드 콘텐츠'];

function Field({ label, required, children, fullWidth }) {
  return (
    <div className={fullWidth ? styles.fieldWrapperFull : styles.fieldWrapper}>
      <div className={styles.fieldLabelRow}>
        <span className={styles.fieldLabel}>{label}</span>
        {required && <span className={styles.fieldLabelRequired}>*</span>}
      </div>
      {children}
    </div>
  );
}

function StepDots({ step, total }) {
  return (
    <div className={styles.stepDotsContainer}>
      {Array.from({ length: total }, (_, i) => {
        const idx = i + 1;
        const done = idx < step;
        const active = idx === step;
        return (
          <div key={i} className={done || active ? styles.stepDotActive : styles.stepDotInactive}>
            {done && (
              <svg width="7" height="5" viewBox="0 0 7 5" fill="none">
                <path d="M1 2.5L2.5 4L6 1" stroke="#090909" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CompleteModal({ onConfirm, onLater }) {
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalBox}>
        {/* Top content */}
        <div className={styles.modalTopContent}>
          <div className={styles.modalIconCircle}>
            <svg width="18" height="12" viewBox="0 0 18 12" fill="none">
              <path d="M1.5 6L6.5 11L16.5 1" stroke="#090909" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className={styles.modalTextBlock}>
            <span className={styles.modalHeading}>
              제작사 프로필 설정 완료
            </span>
            <span className={styles.modalSubtext}>
              기본 제작사 프로필이 생성되었습니다.<br />이제 첫 번째 공고를 만들어보세요.
            </span>
          </div>
        </div>

        {/* Buttons */}
        <div className={styles.modalBtnGroup}>
          <button onClick={onConfirm} className={styles.modalBtnPrimary}>
            첫 공고 만들기
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
              <path d="M1 5.5H10M10 5.5L5.5 1M10 5.5L5.5 10" stroke="#090909" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button onClick={onLater} className={styles.modalBtnSecondary}>
            나중에 하기
          </button>
        </div>
      </div>
    </div>
  );
}

function ProducerOnboardingPage() {
  const navigate = useNavigate();
  const { setProfile, setUserInfo, switchRole } = useAuth();
  const [step, setStep] = useState(1);
  const [showModal, setShowModal] = useState(false);

  // Step 1
  const [companyName, setCompanyName] = useState('');
  const [managerName, setManagerName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [address, setAddress] = useState('');

  // Step 2
  const [bizNumber, setBizNumber] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [customTag, setCustomTag] = useState('');
  const [extraTags, setExtraTags] = useState([]);

  const logoRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleLogoChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setLogoFile(f);
    setLogoPreview(URL.createObjectURL(f));
  };

  const toggleTag = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const addCustomTag = () => {
    const t = customTag.trim();
    if (t && !extraTags.includes(t) && !ACTIVITY_TAGS.includes(t)) {
      setExtraTags(prev => [...prev, t]);
      setSelectedTags(prev => [...prev, t]);
    }
    setCustomTag('');
  };

  const removeExtraTag = (tag) => {
    setExtraTags(prev => prev.filter(t => t !== tag));
    setSelectedTags(prev => prev.filter(t => t !== tag));
  };

  return (
    <PageLayout>
      {showModal && (
        <CompleteModal
          onConfirm={() => navigate('/producer/castings/new')}
          onLater={() => navigate('/producer/dashboard')}
        />
      )}

      <div className={styles.pageWrapper}>
        {/* Page header */}
        <div className={styles.pageHeader}>
          <span className={styles.roleBadge}>제작사</span>
          <span className={styles.pageTitle}>프로필 설정</span>
          <div className={styles.stepDotsWrapper}>
            <StepDots step={step} total={2} />
          </div>
        </div>

        {/* ── STEP 1: 기본 정보 ─────────────────────────────── */}
        {step === 1 && (
          <>
            <div className={styles.sectionHeadingWrapper}>
              <div className={styles.sectionTitle}>기본 정보</div>
              <div className={styles.sectionSubtitle}>기본적인 제작사 정보를 입력하세요.</div>
            </div>

            <div className={`${styles.card} ${styles.cardStep1}`}>
              {/* Row 1: 회사이름 / 담당자 이름 */}
              <div className={styles.fieldRow}>
                <Field label="회사이름" required>
                  <input className={styles.input} placeholder="회사 이름을 입력하세요" value={companyName} onChange={e => { setCompanyName(e.target.value); setErrors(p => ({ ...p, companyName: '' })); }} />
                  {errors.companyName && <span style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '13px', color: '#EF4444' }}>{errors.companyName}</span>}
                </Field>
                <Field label="담당자 이름" required>
                  <input className={styles.input} placeholder="담당자 이름을 입력하세요" value={managerName} onChange={e => { setManagerName(e.target.value); setErrors(p => ({ ...p, managerName: '' })); }} />
                  {errors.managerName && <span style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '13px', color: '#EF4444' }}>{errors.managerName}</span>}
                </Field>
              </div>
              {/* Row 2: 연락처 / 이메일 */}
              <div className={styles.fieldRow}>
                <Field label="담당자 연락처" required>
                  <input className={styles.input} placeholder="연락처를 입력하세요" value={phone} onChange={e => { setPhone(e.target.value); setErrors(p => ({ ...p, phone: '' })); }} />
                  {errors.phone && <span style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '13px', color: '#EF4444' }}>{errors.phone}</span>}
                </Field>
                <Field label="이메일" required>
                  <input className={styles.input} placeholder="이메일을 입력하세요" value={email} onChange={e => { setEmail(e.target.value); setErrors(p => ({ ...p, email: '' })); }} />
                  {errors.email && <span style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '13px', color: '#EF4444' }}>{errors.email}</span>}
                </Field>
              </div>
              {/* Row 3: 웹사이트 */}
              <Field label="웹사이트" fullWidth>
                <input className={styles.input} placeholder="웹사이트 URL을 입력하세요" value={website} onChange={e => setWebsite(e.target.value)} />
              </Field>
              {/* Row 4: 주소 */}
              <Field label="주소" fullWidth>
                <input className={styles.input} placeholder="주소를 입력하세요" value={address} onChange={e => setAddress(e.target.value)} />
              </Field>
            </div>

            <div className={styles.btnGroupColumn}>
              <button
                onClick={() => {
                  const newErrors = {};
                  if (!companyName.trim()) newErrors.companyName = '회사 이름을 입력해주세요.';
                  if (!managerName.trim()) newErrors.managerName = '담당자 이름을 입력해주세요.';
                  if (!phone.trim()) newErrors.phone = '연락처를 입력해주세요.';
                  if (!email.trim()) newErrors.email = '이메일을 입력해주세요.';
                  if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
                  setErrors({});
                  setStep(2);
                }}
                className={styles.btnNext}
              >
                다음 단계: 인증 정보 입력
              </button>
            </div>
          </>
        )}

        {/* ── STEP 2: 인증 정보 ─────────────────────────────── */}
        {step === 2 && (
          <>
            <div className={styles.sectionHeadingWrapper}>
              <div className={styles.sectionTitle}>인증 정보</div>
              <div className={styles.sectionSubtitle}>제작사 인증을 위한 정보를 입력해주세요.</div>
            </div>

            <div className={`${styles.card} ${styles.cardStep2}`}>
              {/* 사업자 등록번호 */}
              <Field label="사업자 등록번호" fullWidth>
                <input className={styles.input} placeholder="사업자 등록번호를 입력하세요" value={bizNumber} onChange={e => setBizNumber(e.target.value)} />
              </Field>

              {/* 회사 로고 */}
              <div className={styles.logoSection}>
                <span className={styles.logoLabel}>회사 로고</span>
                <div onClick={() => logoRef.current?.click()} className={styles.logoUploader}>
                  {logoPreview ? (
                    <img src={logoPreview} alt="logo preview" className={styles.logoPreviewImg} />
                  ) : (
                    <>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M12 4v12M6 10l6-6 6 6" stroke="#A9A9B2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M4 20h16" stroke="#A9A9B2" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      <span className={styles.logoUploaderHint}>
                        로고를 첨부하세요.<br />jpg, png (최대 10mb)
                      </span>
                    </>
                  )}
                </div>
                <input ref={logoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoChange} />
              </div>

              {/* 주요 활동 분야 */}
              <div className={styles.activitySection}>
                <div className={styles.activityLabelRow}>
                  <span className={styles.fieldLabel}>주요 활동 분야</span>
                  <span style={{ fontSize: '13px', color: '#A2A2A2', fontFamily: 'Pretendard, sans-serif' }}>(선택)</span>
                </div>
                <div className={styles.tagsInnerWrapper}>
                  {/* preset tags */}
                  <div className={styles.tagsRow}>
                    {ACTIVITY_TAGS.map(tag => {
                      const selected = selectedTags.includes(tag);
                      return (
                        <button
                          key={tag}
                          onClick={() => toggleTag(tag)}
                          className={selected ? styles.tagBtnSelected : styles.tagBtn}
                        >
                          {tag}
                        </button>
                      );
                    })}
                    {/* extra custom tags */}
                    {extraTags.map(tag => (
                      <button
                        key={tag}
                        onClick={() => removeExtraTag(tag)}
                        className={styles.extraTagBtn}
                      >
                        {tag}
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                          <path d="M1 1l8 8M9 1L1 9" stroke="#A2A2A2" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      </button>
                    ))}
                  </div>
                  {/* custom tag input */}
                  <div className={styles.customTagRow}>
                    <input
                      className={styles.inputWhite}
                      placeholder="직접 입력"
                      value={customTag}
                      onChange={e => setCustomTag(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addCustomTag()}
                    />
                    <button onClick={addCustomTag} className={styles.addTagBtn}>
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path d="M9 3v12M3 9h12" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </button>
                  </div>
                  {/* selected tag chips */}
                  {selectedTags.length > 0 && (
                    <div className={styles.selectedTagsRow}>
                      {selectedTags.map(tag => (
                        <div key={tag} className={styles.selectedTagChip}>
                          {tag}
                          <button
                            onClick={() => {
                              setSelectedTags(prev => prev.filter(t => t !== tag));
                              if (extraTags.includes(tag)) removeExtraTag(tag);
                            }}
                            className={styles.chipRemoveBtn}
                          >
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                              <path d="M1 1l8 8M9 1L1 9" stroke="#A2A2A2" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className={styles.btnGroupColumn}>
              <div className={styles.btnRowStep2}>
                <button onClick={() => setStep(1)} className={styles.btnBack}>
                  이전으로
                </button>
                <button
                  disabled={isSubmitting}
                  onClick={async () => {
                    setIsSubmitting(true);
                    try {
                      const result = await createProducerProfile({
                        companyName,
                        managerName,
                        phone,
                        contactEmail: email,
                        website: website || undefined,
                        address: address || undefined,
                        businessRegNumber: bizNumber || undefined,
                      });
                      if (!result?.id) {
                        throw new Error('PRODUCER_PROFILE_ID_MISSING');
                      }

                      try {
                        setProfile(result.id);
                      } catch {}

                      let logoUrl = null;
                      if (logoFile) {
                        try {
                          const { fileEntityId: logoFileId, url } = await uploadFile(logoFile, 'IMAGE');
                          await updateProducerProfile({ id: result.id, logoFileId });
                          logoUrl = url || null;
                          if (logoUrl) setLogoPreview(logoUrl);
                        } catch {
                          // 로고 업로드 실패는 프로필 생성을 막지 않음
                        }
                      }

                      // 온보딩 완료 후 토큰 재발급 (백엔드가 isProducer=true 세팅 → 새 JWT 필요)
                      try { await reissueToken(); } catch {}
                      try {
                        setUserInfo({ name: companyName, profileImage: logoUrl || undefined, stageName: null, realName: null });
                        switchRole('PRODUCER');
                        localStorage.setItem('isProducer', 'true');
                      } catch {}

                      setShowModal(true);
                    } catch (error) {
                      console.error('[ProducerOnboarding] profile creation failed:', error);
                      alert('프로필 생성에 실패했습니다. 다시 시도해주세요.');
                    } finally {
                      setIsSubmitting(false);
                    }
                  }}
                  className={isSubmitting ? styles.btnSubmitDisabled : styles.btnSubmit}
                >
                  {isSubmitting ? '처리 중...' : '프로필 설정 완료'}
                </button>
              </div>
              <span
                onClick={async () => {
                  if (isSubmitting) return;
                  setIsSubmitting(true);
                  try {
                    const result = await createProducerProfile({
                      companyName,
                      managerName,
                      phone,
                      contactEmail: email,
                      website: website || undefined,
                      address: address || undefined,
                    });
                    if (result?.id) setProfile(result.id);
                    try { await reissueToken(); } catch {}
                    switchRole('PRODUCER');
                    localStorage.setItem('isProducer', 'true');
                  } catch {
                    // 프로필 생성 실패해도 진행
                  } finally {
                    setIsSubmitting(false);
                  }
                  navigate('/castings');
                }}
                className={styles.skipLink}
              >
                건너뛰기
              </span>
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
}

export default ProducerOnboardingPage;
